'use strict';

function FullAssessmentInputModelFactory(careAssessmentConstants, objectUtils, kirkleesCareAssessmentService, careAssessmentService) {

  function getAccumulator(amountFieldName) {
    amountFieldName = amountFieldName || 'amount';

    return function amountAccumulator(total, item) {
      return total + item[amountFieldName];
    };
  }

  function getArrayLengthSafely(obj, arrayPropertyName) {
    return angular.isArray(obj[arrayPropertyName]) ? obj[arrayPropertyName].length : 0;
  }

  /**
   * Encapsulates the retrieval of user inputs from the model when evaluating a short care financial assessment.
   *
   * @param fields the raw model
   * @constructor
   */
  function FullAssessmentInputModel(fields) {

    this.isValid = function () {
      return !!fields.person;
    };

    this.getJointCapital = function () {

      var jointCapitalFields = fields.joint && fields.joint.capital;

      if (!jointCapitalFields) {
        return 0;
      }
      var savingsTotal = jointCapitalFields.hasSavingsInvestments && jointCapitalFields.savingsInvestments ?
        jointCapitalFields.savingsInvestments.reduce(getAccumulator(), 0) : 0;
      var sharesTotal = jointCapitalFields.hasShareholdings && jointCapitalFields.shareholdings ?
        jointCapitalFields.shareholdings.reduce(getAccumulator('totalValue'), 0) : 0;
      return savingsTotal + sharesTotal;
    };

    this.dependentChildrenLivingInProperty = function () {
      return fields.peopleLivingWithYou && fields.peopleLivingWithYou.hasDependentChildren ?
        getArrayLengthSafely(fields.peopleLivingWithYou, 'dependentChildren') : 0;
    };

    this.otherPeopleLivingInProperty = function () {
      return fields.peopleLivingWithYou && fields.peopleLivingWithYou.hasOtherPeople ?
        getArrayLengthSafely(fields.peopleLivingWithYou, 'otherPeople') : 0;
    };

    function getPeriodicAmounts(types, userFieldsPath) {

      var periodicAmounts = [];

      if (!userFieldsPath) {
        return periodicAmounts;
      }

      angular.forEach(types, function (type) {
        if (userFieldsPath[type]) {

          var amount = userFieldsPath[type];
          var period = careAssessmentConstants.fixedPaymentPeriods[type] || userFieldsPath[type + 'Period'];

          periodicAmounts.push({
            name: type,
            amount: amount,
            weeklyAmount: careAssessmentService.toWeeklyAmount(amount, period)
          });
        }
      });

      return periodicAmounts;
    }

    function getPeriodicUpliftables(types, userFieldsPath) {
      var periodicUpliftableAmounts = [];
      if (!userFieldsPath) {
        return periodicUpliftableAmounts;
      }

      angular.forEach(types, function (type) {
        var pathType = userFieldsPath[type];
        if (pathType) {
          var level = careAssessmentConstants[type][pathType] || null;
          var amount = level && level.amount || 0;      

          periodicUpliftableAmounts.push({
            name: type,
            amount: amount,
            weeklyAmount: amount
          });
        }
      });

      return periodicUpliftableAmounts;
    }

    function weeklyTotal(incomeList) {

      if (!incomeList) {
        return 0;
      }

      return incomeList.reduce(function (total, item) {
        return total + careAssessmentService.toWeeklyAmount(item.amount, item.amountPeriod);
      }, 0);
    }

    /**
     * Returns the individual data of either the applicant or their partner
     */
    function getPersonalData(userFields) {

      var singleAmountIncomeTypes = _.without(careAssessmentConstants.incomeTypes, 'earningsFromPaidEmployment', 'privatePension');
      var incomes = [], savingsTotal = 0, sharesTotal = 0;

      if (userFields.income) {
        incomes = getPeriodicAmounts(singleAmountIncomeTypes, userFields.income.benefits);

        var cumulativeWeeklyEmploymentIncome = weeklyTotal(userFields.income.wages);
        incomes.push({
          name: 'earningsFromPaidEmployment',
          amount: cumulativeWeeklyEmploymentIncome,
          weeklyAmount: cumulativeWeeklyEmploymentIncome
        });

        var cumulativeWeeklyPensionIncome = weeklyTotal(userFields.income.privatePensions);
        incomes.push({
          name: 'privatePension',
          amount: cumulativeWeeklyPensionIncome,
          weeklyAmount: cumulativeWeeklyPensionIncome
        });

        var universalCreditIncome = kirkleesCareAssessmentService.calculateUniversalCreditIncome(
          userFields.income.universalCredit);
        incomes.push(universalCreditIncome);

        var upliftableIncomes = getPeriodicUpliftables(careAssessmentConstants.upliftableIncomeTypes, userFields.income.benefits);
        incomes = incomes.concat(upliftableIncomes);     
      }

      if (userFields.capital) {
        var capitalFields = userFields.capital;
        savingsTotal = capitalFields.hasSavingsInvestments && capitalFields.savingsInvestments ?
          capitalFields.savingsInvestments.reduce(getAccumulator(), 0) : 0;
        sharesTotal = capitalFields.hasShareholdings && capitalFields.shareholdings ?
          capitalFields.shareholdings.reduce(getAccumulator('totalValue'), 0) : 0;
      }

      userFields.details = userFields.details || {};

      return {
        dob: userFields.details.dob,
        individualCapital: savingsTotal + sharesTotal,
        incomes: incomes,
        getWeeklyIncome: function (name) {
          var income = _.find(incomes, {name: name});
          return income ? income.weeklyAmount : 0;
        }
      };
    }

    var expenses = getPeriodicAmounts(careAssessmentConstants.expenseTypes, fields.person.expenses);

    this.getWeeklyExpense = function (name) {

      var expense = _.find(expenses, {name: name});
      return expense ? expense.weeklyAmount : 0;
    };

    this.getWeeklyWorkingTaxCredit = function () {
      var amount = objectUtils.getPropertyPath(fields, 'person.income.benefits.workingTaxCredit');
      var period = objectUtils.getPropertyPath(fields, 'person.income.benefits.workingTaxCreditPeriod');
      return amount ? careAssessmentService.toWeeklyAmount(amount, period) : 0;
    };

    this.getApplicantData = function () {
      var applicantData = getPersonalData(fields.person);
      applicantData.isEntitledToCarersPremium = objectUtils.getPropertyPath(fields, 'person.income.isEntitledToCarersPremium');

      var propertyOwnership = objectUtils.getPropertyPath(fields, 'person.details.propertyTenancy');
      applicantData.ownsProperty = propertyOwnership === 'ownedByYou' || propertyOwnership === 'ownedJointly';

      applicantData.hasAdditionalProperties = objectUtils.getPropertyPath(fields, 'person.property.hasAdditionalProperties');

      if (applicantData.hasAdditionalProperties) {
        var additionalProperties = objectUtils.getPropertyPath(fields, 'person.property.additionalProperties');
        if (additionalProperties !== undefined) {
          var propertyEquity = additionalProperties.reduce(
            function (totalEquity, property) {
              return totalEquity + (property.equity || 0);
            }, 0);
          applicantData.individualCapital += propertyEquity;
        }
      }
      return applicantData;
    };

    this.getPartnerData = function () {
      return this.isCouple() ? getPersonalData(fields.partner) : {};
    };

    this.isParterFinanceDeclared = function () {
      return fields.person.details.declarePartnerFinances;
    };

    this.isBenefitPaidAsCouple = function () {
      return fields.person.income && fields.person.income.benefitsPaidAsCouple;
    };

    this.isCouple = function () {
      return fields.person.details.hasPartner;
    };

    this.getCostOfCare = function (careType) {
      return fields.careAssessment && fields.careAssessment[careType] && fields.careAssessment[careType].cost;
    };
  }

  return FullAssessmentInputModel;
}

FullAssessmentInputModelFactory.$inject = ['kirkleesCareAssessmentConstants', 'objectUtils', 'kirkleesCareAssessmentService', 'careAssessmentService'];

angular.module('kerp-forms.forms').factory('FullAssessmentInputModel', FullAssessmentInputModelFactory);
